var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useContext, useEffect, } from "react";
import ActionMenuContext from "./ActionMenuContext";
import { StyledActionMenuItem, StyledActionMenuItemContainer, StyledExternalLink, StyledExternalLinkIcon, StyledExtra, StyledIcon, } from "./ActionMenuItem.style";
import useUniqueId from "../../hooks/useUniqueId";
import { ReverseThemeProvider } from "../../theme/ThemeProvider";
import ColorFlag from "../color-flag/ColorFlag";
import { isSpecialKey, trapEvent } from "../../utils/EventHelpers";
import useIdAttrs from "../../hooks/useIdAttrs";
import { StyledContents } from "../../utils/StyleUtils";
import useForwardedRef from "../../hooks/useForwardedRef";
const prefixId = "nx-actionmenu-item-";
const ActionMenuItem = forwardRef(function ActionMenuItem(_a, ref) {
    var { id: defaultId, "data-insights": insightsAttr, label, iconName, flag, as, to, onClick, onKeyDown, extra, isSelectable = true, disabled = false } = _a, rest = __rest(_a, ["id", "data-insights", "label", "iconName", "flag", "as", "to", "onClick", "onKeyDown", "extra", "isSelectable", "disabled"]);
    const { focusItem, setSelectedItem, setFocusItem, atLeastOneItemWithIcon, onHide } = useContext(ActionMenuContext);
    const [itemRef, setItemRef] = useForwardedRef(ref);
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    useEffect(() => {
        if (focusItem === id && itemRef.current) {
            itemRef.current.focus({ preventScroll: true });
        }
    }, [id, focusItem]);
    // when the menu has quick find, its children are updated (ie. added, removed).
    // the focus should not be set on an item while the user is typing on the search input.
    useEffect(() => () => {
        if (focusItem === id) {
            setFocusItem("");
        }
    }, []);
    const handleOnClick = (e) => {
        if (!to && isSelectable) {
            setSelectedItem(id, e);
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    const handleOnKeyDown = (e) => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        const keyCode = e.key;
        if (keyCode === "Enter" || keyCode === " ") {
            if (as === "span") {
                trapEvent(e);
            }
            isSelectable && setSelectedItem(id, !to ? e : undefined);
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
        }
    };
    const handleOnFocus = () => {
        setFocusItem(id);
    };
    const renderExtra = () => {
        if (to) {
            return (
            // despite aria-hidden, separator role needed to prevent axe complaining
            React.createElement(StyledExtra, { "aria-hidden": "true", role: "separator" },
                React.createElement(StyledExternalLink, Object.assign({}, idAttrs("-external"), { to: to, target: "_blank", tabIndex: -1 }),
                    React.createElement(ReverseThemeProvider, null,
                        React.createElement(StyledExternalLinkIcon, { name: "newTab" })))));
        }
        return (React.createElement(StyledExtra, { onClick: handleOnClick, tabIndex: -1, "aria-hidden": "true", role: "separator" }, extra));
    };
    return (React.createElement(StyledActionMenuItemContainer, { "$disabled": disabled, "$focused": Boolean(focusItem === id) },
        React.createElement(StyledContents, { onClick: (e) => {
                to && (onClick === null || onClick === void 0 ? void 0 : onClick(e));
                // if link being followed we still need to close menu if it wasn't a control click
                !isSpecialKey(e) && to && !disabled && (onHide === null || onHide === void 0 ? void 0 : onHide());
            } },
            React.createElement(StyledActionMenuItem, Object.assign({ role: "menuitem", "data-a11y-ignore": "aria-dialog-name aria-required-children" }, (!disabled && { tabIndex: 0, onKeyDown: handleOnKeyDown, onFocus: handleOnFocus, to }), idAttrs(), { ref: setItemRef }, (!to && !disabled && { onClick: handleOnClick }), { "$shouldUsePlaceholderIfNoIcon": atLeastOneItemWithIcon, "$hasIcon": Boolean(iconName) || Boolean(flag), as: as }, (disabled && { $disabled: disabled, "aria-disabled": true }), rest),
                React.createElement("span", null,
                    iconName && React.createElement(StyledIcon, { name: iconName }),
                    flag && React.createElement(ColorFlag, { color: flag === "none" ? undefined : flag }),
                    label)),
            !disabled && (extra || to) && renderExtra())));
});
export const isActionMenuItem = (child) => {
    if (!child || typeof child === "boolean") {
        return false;
    }
    return child.type === ActionMenuItem;
};
export default ActionMenuItem;
//# sourceMappingURL=ActionMenuItem.js.map